import React, { useEffect, useState } from "react";
import { useAuthBootstrap } from "../../../context/AuthBootstrapContext";
import { useSectionSubmit } from "../lib/useSectionSubmit";
import LabeledInput from "../components/LabeledInput";
import TypicalAvailabilityPicker from "../../OnboardingFlow/TypicalAvailabilityPicker";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL || "https://api-2026.fikacreatives.co";

function FeaturedToggle({ profileAssets }) {
  const dbValue = Boolean(profileAssets?.allow_featured_profile);
  const [on, setOn] = useState(dbValue);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    setOn(Boolean(profileAssets?.allow_featured_profile));
    setSaved(false);
  }, [profileAssets]);

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    setSaveError("");
    try {
      const res = await fetch(`${API_BASE}/api/settings/creative-profile`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ allow_featured_profile: on }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || "Failed to save");
      setSaved(true);
    } catch (err) {
      setSaveError(err.message || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
            Show me in Featured
          </div>
          <div style={{ fontSize: 13, color: "#888", marginTop: 2, lineHeight: 1.4 }}>
            Let other Fika members discover your profile and work in the Featured section and invite you to coffee - limit 2 per month.
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          onClick={() => { setOn((v) => !v); setSaved(false); }}
          style={{
            flexShrink: 0,
            width: 44,
            height: 26,
            borderRadius: 999,
            border: "none",
            background: on ? "#17154f" : "#D5CFC6",
            cursor: "pointer",
            position: "relative",
            transition: "background 0.2s ease",
            padding: 0,
          }}
        >
          <span
            style={{
              position: "absolute",
              top: 3,
              left: on ? 21 : 3,
              width: 20,
              height: 20,
              borderRadius: "50%",
              background: "#fff",
              transition: "left 0.2s ease",
              boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
            }}
          />
        </button>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: "8px 20px",
            background: "#17154f",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        {saved && <span style={{ fontSize: 13, color: "#2d7a4f" }}>Saved ✓</span>}
        {saveError && <span style={{ fontSize: 13, color: "#b42318" }}>{saveError}</span>}
      </div>
    </div>
  );
}

export default function FikaUpdatesSection() {
  const { person, profileAssets, refresh } = useAuthBootstrap();
  const { status, error, submit } = useSectionSubmit({
    endpoint: `${API_BASE}/api/settings/fika-updates`,
    method: "PATCH",
  });

  const [cafe, setCafe] = useState("");
  const [drink, setDrink] = useState("");
  const [availSaved, setAvailSaved] = useState(false);

  useEffect(() => {
    setCafe(profileAssets?.favorite_cafe || person?.favorite_cafe || "");
    setDrink(profileAssets?.favorite_drink || person?.favorite_drink || "");
  }, [profileAssets, person]);

  async function onSave(e) {
    e.preventDefault();
    const result = await submit({ favorite_cafe: cafe, favorite_drink: drink });
    if (result?.ok && typeof refresh === "function") {
      await refresh();
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>

      <FeaturedToggle profileAssets={profileAssets} />

      <div style={{ borderTop: "1px solid #ede8df" }} />

      {/* Cafe + drink */}
      <form onSubmit={onSave} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <p style={{ margin: 0, fontSize: 14, color: "#888", lineHeight: 1.5 }}>
          Update your favorite cafe and what you usually order.
        </p>

        <LabeledInput
          label="Favorite cafe"
          value={cafe}
          placeholder="Good Intentions Coffee"
          onChange={(val) => setCafe(val)}
        />

        <LabeledInput
          label="Usual order"
          value={drink}
          placeholder="Oat milk latte"
          onChange={(val) => setDrink(val)}
        />

        {error && <p style={{ margin: 0, fontSize: 13, color: "#b42318" }}>{error}</p>}

        <div>
          <button
            type="submit"
            disabled={status === "saving"}
            style={{
              padding: "10px 24px",
              background: "#17154f",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: status === "saving" ? "not-allowed" : "pointer",
              opacity: status === "saving" ? 0.6 : 1,
            }}
          >
            {status === "saving" ? "Saving…" : "Save"}
          </button>
          {status === "saved" && (
            <span style={{ marginLeft: 12, fontSize: 13, color: "#2d7a4f" }}>Saved ✓</span>
          )}
        </div>
      </form>

      {/* Typical availability */}
      <div>
        <p style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 600, color: "#1a1a2e" }}>
          Typical availability
        </p>
        <p style={{ margin: "0 0 16px", fontSize: 14, color: "#888", lineHeight: 1.5 }}>
          When are you usually free to meet other creatives? This helps us schedule your Fikas.
        </p>
        <TypicalAvailabilityPicker
          initialSlots={profileAssets?.typical_availability}
          alwaysShow
          buttonLabel="Save availability"
          onSubmit={async () => {
            setAvailSaved(true);
            if (typeof refresh === "function") await refresh();
          }}
        />
        {availSaved && (
          <p style={{ marginTop: 8, fontSize: 13, color: "#2d7a4f" }}>Availability saved ✓</p>
        )}
      </div>
    </div>
  );
}
